/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useState, useEffect } from 'react';
import _ from 'lodash';

import PropTypes from 'prop-types';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useTranslator } from '@jutro/locale';
import { MLPaymentService } from 'ml-services-common-react';
import { MLModalService } from 'ml-portals-services-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import appConfig from 'app-config';
import '@xengage/gw-platform-translations';

import checkImage from 'ml-digital-theme-styles/images/cakeandarrow/svg/check.svg';
import { translateContent } from 'ml-components-platform-react';
import MLNameDetailsComponent from '../MLNameDetailsComponent/MLNameDetailsComponent';
import messages from './MLBankAccountDetailsComponent.messages';
import metadata from './MLBankAccountDetailsComponent.metadata.json5';
import styles from './MLBankAccountDetailsComponent.module.scss';

// ML: MLBankAccountDetailsDirectiveCtrl.js, ml-bank-account-details.html
function MLBankAccountDetailsComponent(props) {
    const translator = useTranslator();
    const isQnB = appConfig.portalCode === 'QB';

    const {
        id,
        onValidate,
        showValidationErrors,
        bankAccountDetails,
        instrumentType,
        onModelChange,
        onSave,
        onCancel,
        showCancel,
        showErrorPopup,
        setErrorTimestamp,
        errorTimestamp
    } = props;

    const {
        showErrorModal
    } = MLModalService();

    const [showErrors, updateShowErrors] = useState(showValidationErrors);

    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const { authHeader } = useAuthentication();

    const gpaMode = appConfig.portalCode === 'GPA';

    const onRoutingNumberChange = (value) => {
        bankAccountDetails.inEditMode.value = false;
        bankAccountDetails.customerBankName.value = undefined;
        bankAccountDetails.accountRoutingNumber.value = value;

        if (bankAccountDetails.accountRoutingNumber.aspects.valid) {
            MLPaymentService.getCustomerBankNameByRoutingNumber(bankAccountDetails.accountRoutingNumber.value, authHeader).then((bankName) => {
                if (typeof bankName !== 'object' && bankName !== undefined) {
                    bankAccountDetails.isRoutingNumberValid.value = true;
                    bankAccountDetails.customerBankName.value = bankName;
                } else {
                    bankAccountDetails.isRoutingNumberValid.value = false;
                }
                bankAccountDetails.inEditMode.value = true;
                onModelChange(bankAccountDetails);
            }).catch(() => {
                if (showErrorPopup) showErrorModal(messages.ServiceErrorTitle, messages.ServiceErrorMessage);
            });
        } else {
            onModelChange(bankAccountDetails);
        }
    };

    useEffect(() => {
        if (bankAccountDetails.accountRoutingNumber.aspects.valid) {
            MLPaymentService.getCustomerBankNameByRoutingNumber(bankAccountDetails.accountRoutingNumber.value, authHeader).then((bankName) => {
                if (typeof bankName !== 'object' && bankName !== undefined) {
                    bankAccountDetails.isRoutingNumberValid.value = true;
                    bankAccountDetails.customerBankName.value = bankName;
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isAccountType = (type = 'checking') => {
        if (bankAccountDetails.accountType && bankAccountDetails.accountType.value) {
            return bankAccountDetails.accountType.value.code === type;
        }
        return false;
    };

    const onSaveDetails = async () => {
        if (!isComponentValid) {
            // JUT-5609 Closing a dialog closes all open dialogs
            if (showErrorPopup){ 
                showErrorModal(messages.InvalidBankDetailsTitle, messages.InvalidBankDetailsMessage).catch(() => {
                    if(setErrorTimestamp && errorTimestamp !== undefined) {
                        setErrorTimestamp(errorTimestamp+1)
                    }
                });
            }
            updateShowErrors(true);
            if(!showErrorPopup && setErrorTimestamp && errorTimestamp !== undefined) {
                setErrorTimestamp(errorTimestamp+1)
            }
            return;
        }
        if (onSave) {
            onSave(bankAccountDetails, instrumentType);
        }
    };

    const resolvers = {
        resolveComponentMap: {
            mlnamedetailscomponent: MLNameDetailsComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onSaveDetails: onSaveDetails,
            onCancel: onCancel
        }
    };

    const renderTooltip = () => {
        if (!isAccountType('savings')) return

        return {
            renderContent: () => translateContent({
                tooltipHeader: translator(messages.RoutingNumberHelperText),
                tooltipContent: translator(messages.RoutingNumberTooltipContent),
                translator
            })
        }
    }

    const overrides = {
        accountHolderComponent: {
            personVM: bankAccountDetails.accountHolder,
            changeNameLabel: translator(messages.NameOfAccountHolder),
            onValidate: setComponentValidation,
            showValidationErrors: showErrors,
            onModelChange
        },
        checkImageRow: {
            visible: isAccountType('checking')
        },
        checkImage: {
            src: checkImage
        },
        routingNumber: {
            onValueChange: onRoutingNumberChange,
            tooltip: renderTooltip()
        },
        bankNameRow: {
            visible: !!bankAccountDetails.customerBankName.value
        },
        bankName: {
            content: bankAccountDetails.customerBankName.value
        },
        actionRow: {
            className: isQnB ? "row col-md-12 vertical-spacing-30 mobilerow" : "row col-md-3 vertical-spacing-30"
        },
        saveButton: {
            visible: !_.isUndefined(onSave),
            className: isQnB ? "saveButton primaryButton" : "saveButton"
        },
        cancelButton: {
            visible: showCancel,
            className: isQnB ? "saveButton secondaryButton widthSmall" : "saveButton"
        }
    };

    useEffect(() => {
        const confirmAccountNumber = document.querySelector('.disable-copy-paste-actions');
        confirmAccountNumber.onpaste = (e) => e.preventDefault();
    });

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            model={bankAccountDetails}
            onValidationChange={setComponentValidation}
            showErrors={showErrors}
            onModelChange={onModelChange}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

MLBankAccountDetailsComponent.propTypes = {
    id: PropTypes.string,
    onValidate: PropTypes.func,
    showValidationErrors: PropTypes.bool,
    bankAccountDetails: PropTypes.shape({
        accountHolder: PropTypes.shape({}),
        accountType: PropTypes.shape({
            value: PropTypes.shape({
                code: PropTypes.string
            })
        }),
        inEditMode: PropTypes.shape({
            value: PropTypes.bool
        }),
        customerBankName: PropTypes.shape({
            value: PropTypes.string
        }),
        accountRoutingNumber: PropTypes.shape({
            aspects: PropTypes.shape({
                valid: PropTypes.bool
            }),
            value: PropTypes.string
        }),
        isRoutingNumberValid: PropTypes.shape({
            value: PropTypes.bool
        })
    }).isRequired,
    instrumentType: PropTypes.string,
    onModelChange: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    showCancel: PropTypes.bool,
    showErrorPopup: PropTypes.bool
};

MLBankAccountDetailsComponent.defaultProps = {
    id: undefined,
    onValidate: undefined,
    showValidationErrors: undefined,
    instrumentType: 'recurring',
    onCancel: _.noop,
    showCancel: false,
    showErrorPopup: true
};

export default MLBankAccountDetailsComponent;
